/**
 * Resume abandoned payment (Sprint 89).
 * Routed at /resume/:intent_id. Fetches the intent + re-opens the
 * Razorpay checkout sheet using the same order_id, so the customer
 * completes payment without re-entering anything.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, AlertCircle, CheckCircle2, Package } from "lucide-react";
import api from "../services/api";

export default function ResumePayment() {
  const { intent_id } = useParams();
  const [state, setState] = useState({ loading: true, intent: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/payments/resume/${intent_id}`);
        if (cancelled) return;
        setState({ loading: false, intent: data, error: null });
      } catch (e) {
        if (cancelled) return;
        setState({ loading: false, intent: null, error: e?.response?.data?.detail || "Link invalid or expired" });
      }
    })();
    return () => { cancelled = true; };
  }, [intent_id]);

  const openCheckout = () => {
    const { intent } = state;
    if (!intent) return;
    if (intent.mock || !intent.key_id) {
      alert("This is a mock checkout. In production, Razorpay would open here.");
      return;
    }
    // Razorpay JS SDK should already be loaded by the main booking page.
    if (typeof window.Razorpay !== "function") {
      const s = document.createElement("script");
      s.src = "https://checkout.razorpay.com/v1/checkout.js";
      s.onload = () => _open(intent);
      document.body.appendChild(s);
      return;
    }
    _open(intent);
  };

  const _open = (intent) => {
    const rp = new window.Razorpay({
      key: intent.key_id,
      order_id: intent.intent_id,
      amount: intent.amount_paise,
      currency: "INR",
      name: "PickNGo",
      description: intent.order_summary?.package_description || "Express courier booking",
      handler: async (resp) => {
        try {
          await api.post("/payments/confirm-online", {
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          window.location.href = "/orders?recovered=1";
        } catch (e) {
          alert("Payment verified but order creation failed. Contact support with payment ID " + resp.razorpay_payment_id);
        }
      },
    });
    rp.open();
  };

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="resume-loading">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full rounded-xl border border-red-200 bg-red-50 p-6 text-center" data-testid="resume-error">
          <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <h2 className="text-lg font-semibold text-red-900 mb-1">Link expired</h2>
          <p className="text-sm text-red-700">{state.error}</p>
          <a href="/orders" className="inline-block mt-4 px-4 py-2 rounded-lg bg-[#FF6B00] text-white text-sm font-medium no-underline">
            Start a new booking
          </a>
        </div>
      </div>
    );
  }

  const i = state.intent;
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="max-w-md w-full rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="resume-card">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 rounded-lg bg-orange-50 flex items-center justify-center">
            <Package className="h-5 w-5 text-[#FF6B00]" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Complete your booking</h2>
            <p className="text-xs text-slate-500">Your details are saved — one click to finish.</p>
          </div>
        </div>
        <div className="rounded-xl bg-slate-50 p-4 space-y-2 text-sm mb-5">
          {i.order_summary?.pickup_address && (
            <div><span className="text-slate-500 text-xs uppercase tracking-wider">From</span><p className="text-slate-900">{i.order_summary.pickup_address}</p></div>
          )}
          {i.order_summary?.delivery_address && (
            <div><span className="text-slate-500 text-xs uppercase tracking-wider">To</span><p className="text-slate-900">{i.order_summary.delivery_address}</p></div>
          )}
          {i.order_summary?.package_description && (
            <div><span className="text-slate-500 text-xs uppercase tracking-wider">Parcel</span><p className="text-slate-900">{i.order_summary.package_description}</p></div>
          )}
        </div>
        <div className="flex items-center justify-between rounded-xl bg-orange-50 border border-orange-100 px-4 py-3 mb-5">
          <span className="text-sm text-slate-600">Amount</span>
          <span className="text-xl font-bold text-[#FF6B00]">₹{(i.amount_inr || 0).toLocaleString("en-IN")}</span>
        </div>
        <button
          onClick={openCheckout}
          className="w-full px-4 py-3 rounded-xl bg-[#FF6B00] text-white font-semibold hover:bg-[#E55A00] flex items-center justify-center gap-2"
          data-testid="resume-pay-btn"
        >
          <CheckCircle2 className="h-4 w-4" /> Pay ₹{(i.amount_inr || 0).toLocaleString("en-IN")} & Confirm
        </button>
        <p className="text-[11px] text-slate-400 text-center mt-3">Secured by Razorpay · Link expires 72h after creation</p>
      </div>
    </div>
  );
}
